import { Link } from 'react-router-dom';
import { usePageMeta } from '../../hooks/usePageMeta';
import { useI18nLocaleSync } from '../../hooks/useI18nLocaleSync';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';

export function HowPowerballWorks() {
  const lp = useLocalizedPath();
  useI18nLocaleSync();
  usePageMeta({
    title: 'How Powerball Works – Complete Guide | Lotto Number Generator',
    description:
      'A complete guide to Powerball: how to play, prize tiers, jackpot odds, annuity vs lump sum, Power Play, and how drawings are conducted.',
    path: '/articles/how-powerball-works',
  });

  return (
    <main className="content">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to={lp('/')}>Home</Link>
        <span className="sep">/</span>
        <Link to="/articles/">Articles</Link>
        <span className="sep">/</span>
        <span className="current">How Powerball Works</span>
      </nav>

      <article className="about-section">
        <h1>How Powerball Works: A Complete Guide</h1>
        <p>
          Powerball is the largest and most-played lottery game in the United States, famous for
          producing some of the biggest jackpots in world history—including the all-time record of{' '}
          <strong>$2.04 billion</strong> in November 2022. Whether you are playing for the first time
          or simply curious about how the game is structured, this guide explains everything: the
          rules, the odds, the prize tiers, and the mechanics behind every drawing.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>A Brief History of Powerball</h2>
        <p>
          Powerball traces its roots to 1988, when the Multi-State Lottery Association (MUSL) launched
          a game called <em>Lotto America</em> to allow multiple state lotteries to share a common
          jackpot pool. In April 1992, Lotto America was rebranded as Powerball and introduced the
          two-drum format that defines the game today—one drum for the main white balls and a separate
          drum for the red Powerball. This structure allows for longer jackpot odds and therefore
          larger prize accumulations than single-drum games.
        </p>
        <p>
          Over the following decades, the game expanded its reach and periodically adjusted its number
          ranges to increase jackpot sizes. The most significant change came in October 2015, when the
          main ball pool expanded from 59 to 69 numbers while the Powerball pool shrank from 35 to 26.
          This change made the jackpot odds nearly three times longer—from 1 in 175 million to 1 in
          292 million—resulting in jackpots that roll over far more often and reach historically
          unprecedented levels.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>How to Play Powerball</h2>
        <p>
          Playing Powerball requires selecting two sets of numbers. First, pick{' '}
          <strong>5 main numbers from 1 to 69</strong>. These are represented by white balls in the
          drawing drum. Second, pick <strong>1 Powerball number from a separate pool of 1 to 26</strong>.
          This red ball is drawn from a completely independent drum, meaning the Powerball number can
          duplicate any of your five main numbers without issue.
        </p>
        <p>
          Tickets cost <strong>$2 each</strong> and can be purchased at authorized retailers in the 45
          participating states, Washington D.C., Puerto Rico, and the U.S. Virgin Islands. You can
          choose your own numbers using a playslip, or request a <em>Quick Pick</em> (also called Easy
          Pick) where the terminal randomly selects all numbers for you. Quick Picks account for the
          majority of tickets sold and, statistically speaking, win at the same rate as self-chosen
          numbers—because all combinations have identical odds.
        </p>
        <p>
          Drawings are held <strong>three times per week</strong>: Monday, Wednesday, and Saturday
          evenings at 10:59 p.m. Eastern Time. Tickets must be purchased before the cutoff time in
          your jurisdiction, which is typically 59 minutes to a few hours before each drawing.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Prize Tiers and Odds</h2>
        <p>
          Powerball has <strong>nine prize tiers</strong>. Matching all five main numbers plus the
          Powerball wins the jackpot. You don't need to match all numbers to win a prize—even matching
          only the Powerball number (with no main numbers) pays $4. Here is a complete breakdown:
        </p>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.9, color: 'var(--text-secondary)' }}>
          <li><strong>Jackpot</strong>: 5 main + Powerball — odds 1 in 292,201,338</li>
          <li><strong>$1,000,000</strong>: 5 main numbers, no Powerball — odds 1 in 11,688,053</li>
          <li><strong>$50,000</strong>: 4 main + Powerball — odds 1 in 913,129</li>
          <li><strong>$100</strong>: 4 main numbers, no Powerball — odds 1 in 36,525</li>
          <li><strong>$100</strong>: 3 main + Powerball — odds 1 in 14,494</li>
          <li><strong>$7</strong>: 3 main numbers, no Powerball — odds 1 in 580</li>
          <li><strong>$7</strong>: 2 main + Powerball — odds 1 in 701</li>
          <li><strong>$4</strong>: 1 main + Powerball — odds 1 in 92</li>
          <li><strong>$4</strong>: Powerball only — odds 1 in 38</li>
        </ul>
        <p style={{ marginTop: 12 }}>
          The overall odds of winning <em>any</em> prize are approximately{' '}
          <strong>1 in 24.9</strong>. Keep in mind that non-jackpot prizes are fixed amounts, while
          the jackpot grows with each rollover and is split equally among all jackpot-winning tickets
          if more than one winner occurs in the same drawing.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Power Play: Multiplying Non-Jackpot Prizes</h2>
        <p>
          For an extra <strong>$1 per ticket</strong>, players can add the Power Play option, which
          multiplies all non-jackpot prizes by a randomly drawn multiplier. Before each drawing, a
          separate wheel determines the Power Play value: 2×, 3×, 4×, 5×, or 10×. The 10× multiplier
          is only available when the advertised jackpot is $150 million or less. The $1,000,000
          second-tier prize is always doubled to $2,000,000 with Power Play, regardless of the
          multiplier drawn. Power Play does not affect the jackpot itself.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Jackpot Payout: Annuity vs. Lump Sum</h2>
        <p>
          When the jackpot is won, the winner chooses between two payout options. The{' '}
          <strong>annuity option</strong> pays the advertised jackpot amount as 30 graduated annual
          payments over 29 years, with each payment increasing by 5% over the previous one. The{' '}
          <strong>cash option</strong> (lump sum) pays approximately 60% of the advertised jackpot as
          a single immediate payment—the difference reflecting the present value of the future annuity
          stream and the prize reserve fund mechanics.
        </p>
        <p>
          Both options are subject to federal income tax and, in most states, state income tax as well.
          The effective tax rate at the federal level can reach 37% for large jackpots. Most winners
          historically choose the cash option, though the annuity provides a higher total payout over
          time and protects against spending the entire prize immediately.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>How Drawings Are Conducted</h2>
        <p>
          Powerball drawings are conducted by the Florida Lottery on behalf of MUSL in Tallahassee,
          Florida. Each drawing uses a lottery draw machine—either gravity pick or air mix—to randomly
          select 5 white balls from a drum of 69 numbered balls, followed by 1 red Powerball from a
          separate drum of 26 balls. At least two drawing officials must be present along with an
          independent auditor. The equipment is tested and certified before and after each drawing to
          ensure integrity.
        </p>
        <p>
          Drawings are broadcast live on multiple television stations and are also available via
          livestream. Results are published on the official Powerball website immediately after the
          drawing, and state lottery websites update their databases within minutes.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Notable Jackpots in Powerball History</h2>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.9, color: 'var(--text-secondary)' }}>
          <li><strong>$2.04 billion</strong> — November 7, 2022. Single winner in California; the largest lottery prize ever awarded.</li>
          <li><strong>$1.586 billion</strong> — January 13, 2016. Three winners in California, Florida, and Tennessee; the first jackpot to surpass $1 billion.</li>
          <li><strong>$768.4 million</strong> — March 27, 2019. Single winner in Wisconsin.</li>
          <li><strong>$730 million</strong> — January 20, 2021. Single winner in Maryland.</li>
          <li><strong>$699.8 million</strong> — October 4, 2021. Single winner in California.</li>
        </ul>
        <p style={{ marginTop: 12 }}>
          The frequency and size of large jackpots has increased dramatically since the 2015 rule
          change. Before October 2015, the largest Powerball jackpot was $590.5 million (May 2013).
          The new odds structure—combined with increased ticket sales in a growing number of
          jurisdictions—has made billion-dollar jackpots a realistic possibility in any extended roll.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Generating Your Powerball Numbers</h2>
        <p>
          Our free{' '}
          <Link to={lp('/')}>Powerball number generator</Link> produces random combinations using
          JavaScript's <code>Math.random()</code> function within the official Powerball ranges (5
          numbers from 1–69, plus 1 from 1–26). Every generated combination is immediately checked
          against our database of 4,000+ real past Powerball draws—if your numbers exactly match a
          documented jackpot-winning combination from history, the draw date and details are displayed.
          This is for entertainment and curiosity only; no method of picking numbers changes your
          mathematical odds of winning.
        </p>

        <div className="disclaimer" style={{ marginTop: 28 }}>
          <strong>Disclaimer:</strong> For entertainment only. This site is not affiliated with
          Powerball, MUSL, or any official lottery operator. Always verify rules and jackpot amounts
          at the official Powerball website before purchasing tickets.
        </div>

        <p style={{ marginTop: 20 }}>
          <Link to="/articles/">← Back to all articles</Link>
          {' · '}
          <Link to={lp('/guide')}>Lottery Guide</Link>
          {' · '}
          <Link to="/articles/understanding-lottery-odds/">Understanding Lottery Odds</Link>
        </p>
      </article>
    </main>
  );
}
